// client/src/scenes/users/FineIssueDialog.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  MenuItem,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import { gql, useMutation, useQuery } from "@apollo/client";
import { groupClient } from "state/apolloClient";

// --- GraphQL ---
const GET_FINE_TYPES = gql`
  query {
    fineTypes {
      id
      name
      defaultAmount
      paymentWindowDays
      isActive
      description
      groupId
      createdBy
      createdDate
      updatedBy
      updatedDate
    }
  }
`;

const CREATE_MEMBER_FINE = gql`
  mutation CreateMemberFine($newMemberFine: MemberFinesInput!) {
    createMemberFine(newMemberFine: $newMemberFine) {
      id
      userId
      fineTypeId
      amountDue
      amountPaid
      dueDate
      statusId
      description
      createdBy
      createdDate
      updatedBy
      updatedDate
      groupId
    }
  }
`;

// Helper for yyyy-mm-dd (GraphQL Date-friendly)
const yyyyMmDd = (d = new Date()) =>
  new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);

const FineIssueDialog = ({ open, onClose, user, onCreated }) => {
  const [fineTypeId, setFineTypeId] = useState("");
  const [amountDue, setAmountDue] = useState("");
  const [dueDate, setDueDate] = useState(yyyyMmDd());
  const [description, setDescription] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // Only admins/managers can issue fines
  const roleGroupId = Number(localStorage.getItem("roleGroupId"));
  const canManage = roleGroupId === 1 || roleGroupId === 2;

  // Load fine types from GROUP service
  const { data, loading: loadingTypes, error: typesError } = useQuery(GET_FINE_TYPES, {
    client: groupClient,
    skip: !open,
    fetchPolicy: "cache-and-network",
  });

  const fineTypes = useMemo(
    () => (data?.fineTypes || []).filter((f) => f.isActive),
    [data]
  );

  // Prefill amount when type changes
  useEffect(() => {
    const ft = fineTypes.find((f) => String(f.id) === String(fineTypeId));
    if (ft && (amountDue === "" || Number(amountDue) === 0)) {
      setAmountDue(ft.defaultAmount ?? "");
    }
  }, [fineTypeId, fineTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset on close
  useEffect(() => {
    if (!open) {
      setFineTypeId("");
      setAmountDue("");
      setDueDate(yyyyMmDd());
      setDescription("");
      setErrorMsg("");
    }
  }, [open]);

  const [createFine, { loading: creating }] = useMutation(CREATE_MEMBER_FINE, {
    client: groupClient,
    errorPolicy: "all",
    onError: (e) => {
      const m =
        e?.graphQLErrors?.[0]?.extensions?.message ||
        e?.graphQLErrors?.[0]?.message ||
        e?.message ||
        "Unexpected error.";
      setErrorMsg(m);
    },
    onCompleted: (res) => {
      if (res?.createMemberFine?.id) {
        onCreated?.(res.createMemberFine);
        onClose?.();
      } else {
        setErrorMsg("Failed to create member fine.");
      }
    },
  });

  const canSubmit =
    !!user?.id &&
    !!fineTypeId &&
    dueDate &&
    amountDue !== "" &&
    Number(amountDue) > 0 &&
    canManage;

  const handleSubmit = () => {
    if (!canSubmit) return;
    // NOTE:
    // Back-end sample omitted userId in input. Most systems require it.
    // If your schema rejects `userId` as unknown, remove the `userId` line below.

    // Convert date-only (YYYY-MM-DD) to ISO DateTime so HotChocolate's DateTime scalar can parse it.   
    const dueDateIso = `${dueDate}T00:00:00Z`; // midnight UTC; alternatively: `${dueDate}T00:00:00`


    const variables = {
      newMemberFine: {
        userId: user.id, // remove if your schema infers it server-side
        fineTypeId: Number(fineTypeId),
        amountDue: Number(amountDue),
        statusId: 0, // default to 'Unpaid'; adjust if your setup differs
        amountPaid: null, // let BE default to 0 if preferred
        dueDate: dueDateIso,   
        description: description || null,
      },
    };
    createFine({ variables });
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Issue Fine {user ? `to ${user.firstName} ${user.lastName}` : ""}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Fine Type"
                value={fineTypeId}
                onChange={(e) => setFineTypeId(e.target.value)}
                disabled={loadingTypes || !canManage}
                helperText={typesError ? "Failed to load fine types." : ""}
              >
                {fineTypes.map((ft) => (
                  <MenuItem key={ft.id} value={ft.id}>
                    {ft.name} {ft.defaultAmount != null ? `• KES ${Number(ft.defaultAmount).toLocaleString()}` : ""}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                type="number"
                fullWidth
                label="Amount Due (KES)"
                value={amountDue}
                onChange={(e) => setAmountDue(e.target.value)}
                inputProps={{ min: 0, step: "0.01" }}
                disabled={!canManage}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                type="date"
                fullWidth
                label="Due Date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={!canManage}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description (optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                minRows={2}
                disabled={!canManage}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Tooltip title={canManage ? "" : "You are not authorized to issue fines."}>
            <span>
              <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit || creating}>
                {creating ? "Issuing..." : "Issue Fine"}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!errorMsg}
        autoHideDuration={5000}
        onClose={() => setErrorMsg("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setErrorMsg("")}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default FineIssueDialog;
